import { Box, Button, Menu, MenuItem } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAvailableSecurityAdminsSites } from "src/features/security/hooks/api/use-available-security-admins-sites";
import { usePopover } from "src/hooks/use-popover";
import useStore from "src/hooks/use-store";
import { ChevronDown as ChevronDownIcon } from "src/icons/chevron-down";
import { Query } from "../atoms/query";
import { t } from "i18next";
import { Scrollbar } from "../atoms/scrollbar";

const SEARCH_INPUT_THRESHOLD = 5;

const SecuritySelectorComponent = () => {
  const { user } = useStore();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [anchorRef, open, handleOpen, handleClose] = usePopover();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const firstMenuItemRef = useRef<HTMLLIElement | null>(null);

  const [searchQuery, setSearchQuery] = useState("");

  const securityAvailableSites = useAvailableSecurityAdminsSites();

  const selectedSite = securityAvailableSites.data?.find(site => site.token === user.siteToken);

  const isSearchInputThresholdCrossed = useMemo(
    () => securityAvailableSites?.data?.length > SEARCH_INPUT_THRESHOLD,
    [securityAvailableSites],
  );

  const filteredSites = useMemo(
    () =>
      securityAvailableSites?.data?.filter(item =>
        item.name.toLowerCase().includes(searchQuery.trim().toLowerCase()),
      ),
    [securityAvailableSites, searchQuery],
  );

  useEffect(() => {
    if (!open) return;

    setTimeout(() => {
      isSearchInputThresholdCrossed ? inputRef.current?.focus() : firstMenuItemRef.current?.focus();
    });
  }, [open, isSearchInputThresholdCrossed]);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: {
            sm: "center",
            md: "flex-start",
          },
          flexDirection: {
            sm: "row",
            md: "column",
          },
          justifyContent: {
            sm: "flex-start",
            md: "space-between",
          },
          gap: {
            sm: "10px",
            md: "0",
          },
        }}>
        <>
          <Button
            color="primary"
            onClick={handleOpen}
            ref={anchorRef}
            size="large"
            variant="text"
            endIcon={
              <ChevronDownIcon
                fontSize="small"
                sx={{
                  transform: open ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s ease-in",
                }}
              />
            }>
            {selectedSite?.name ?? "---"}
          </Button>

          <Menu
            anchorEl={anchorRef.current}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            open={open}
            onClose={() => {
              handleClose();
              setSearchQuery("");
            }}
            slotProps={{
              list: {
                sx: {
                  ...(isSearchInputThresholdCrossed ? { pt: 0 } : {}),
                },
              },
              paper: {
                sx: {
                  border: "1px solid",
                  borderColor: "divider",
                  mt: 0,
                },
              },
            }}>
            {isSearchInputThresholdCrossed && (
              <Box
                sx={{
                  position: "sticky",
                  top: 0,
                  backgroundColor: "background.paper",
                  zIndex: 1,
                  outline: "none",
                  borderBottom: `1px solid`,
                  borderColor: "divider",
                }}
                onFocus={() => {
                  inputRef.current?.focus();
                }}>
                <Query
                  textInputRef={inputRef}
                  disabled={false}
                  searchAsType
                  onChange={newQuery => setSearchQuery(newQuery)}
                  value={searchQuery}
                  sx={{
                    border: "none",
                    borderRadius: 0,
                    py: 2.5,
                    minWidth: "100%",
                    width: "min-content",
                  }}
                  onKeyDown={e => {
                    if (e.key !== "ArrowDown") {
                      e.stopPropagation();
                    } else {
                      setTimeout(() => {
                        firstMenuItemRef.current?.focus();
                      }, 0);
                    }
                  }}
                />
              </Box>
            )}

            <Scrollbar
              sx={{
                maxHeight: "60vh",
                width: "max-content",
                pt: isSearchInputThresholdCrossed ? 1 : 0,
              }}>
              {filteredSites?.length > 0 ? (
                filteredSites?.map((site, idx) => {
                  return (
                    <MenuItem
                      ref={idx === 0 ? firstMenuItemRef : null}
                      key={site.token}
                      onClick={async () => {
                        user.clearMeilisearchToken();
                        try {
                          await queryClient.invalidateQueries();
                        } catch (err) {}

                        user.setSiteToken(site.token);
                        user.setSiteTimeZone(site?.timezone || "Europe/Warsaw");

                        setTimeout(() => {
                          navigate("/reservations");
                        }, 100);

                        handleClose();
                        setSearchQuery("");
                      }}
                      sx={{ minWidth: "fit-content" }}>
                      {site.name}
                    </MenuItem>
                  );
                })
              ) : (
                <MenuItem disabled>{t("common:noResults")}</MenuItem>
              )}
            </Scrollbar>
          </Menu>
        </>
      </Box>
    </>
  );
};

export const SecuritySelector = observer(SecuritySelectorComponent);
